import { createContext, useContext, useEffect, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import config from "../config";

interface LinksContextType {
  myAccountLink: string | null;
  olsLink: string | null;
  k12ZoneLink:  string | null;
}

const LinksContext = createContext<LinksContextType | undefined>(undefined);

export const LinksProvider = ({ children }: { children: React.ReactNode }) => {
  const { authState } = useOktaAuth(); 
  const oktaAccessToken = authState?.accessToken?.accessToken; 

  const [myAccountLink, setMyAccountLink] = useState<string | null>(null);
  const [olsLink, setOlsLink] = useState<string | null>(null);
  const [k12ZoneLink, setK12ZoneLink] = useState<string | null>(null);
  const QUICKLINKS_API_URL = config.api.endpoints.QUICKLINKS;

  /**
   * Fetch myaccount & OLS quicklinks from API
  */
  const fetchQuickLink = async (type: "myaccount" | "olsLink" | "k12zone") => {
    if (!oktaAccessToken) {
      console.warn(" No Okta token available. Skipping API call.");
      return null;
    }
    try {
      const response = await fetch(`${QUICKLINKS_API_URL}/${type}`,
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${oktaAccessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(` API Error (${response.status}): ${response.statusText}`);
      }
      return await response.text(); 
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
      return null;
    }
  };

  useEffect(() => {
    if (!oktaAccessToken) return; 

    const fetchLinks = async () => {
      const [myAccount, ols, k12zone] = await Promise.all([
        fetchQuickLink("myaccount"),
        fetchQuickLink("olsLink"),
        fetchQuickLink("k12zone"),   //K12 Zone
      ]);
  
      setMyAccountLink(myAccount);
      setOlsLink(ols);
      setK12ZoneLink(k12zone);       //K12 Zone
    };

    fetchLinks();
  }, [oktaAccessToken]); 

  return (
    <LinksContext.Provider value={{ myAccountLink, olsLink, k12ZoneLink }}>
      {children}
    </LinksContext.Provider>
  );
};

export const useLinksContext = () => {
  const context = useContext(LinksContext);
  if (!context) {
    throw new Error("useLinksContext must be used within a LinksProvider");
  }
  return context;
};
